'use client';

import { ComponentProps, FC, ReactNode } from 'react';

import { SubmitButton } from 'src/general/components/Button/SubmitButton';
import { FormGenericError } from 'src/general/components/forms/FormGenericError/FormGenericError';
import { FormWithAction } from 'src/general/components/forms/FormWithAction/FormWithAction';
import { isExternalUrl, processUrlParams } from 'src/general/helpers/validateUrlParams';
import { useFormStepTransition } from 'src/general/transitions/FormStepTransition/FormStepTransition.context';

type Props = Omit<ComponentProps<typeof FormWithAction>, 'onSuccess'> & {
  onSuccess?: ComponentProps<typeof FormWithAction>['onSuccess'];
  CustomFooter?: ReactNode;
  hideFormError?: boolean;
  /** When set, replaces the default Continue label on the submit control. */
  submitLabel?: ReactNode;
};

export const StepForm: FC<Props> = ({
  action,
  renderContent,
  withRecaptcha,
  onSuccess,
  hideFormError,
  CustomFooter,
  submitLabel,
}) => {
  const { navigate } = useFormStepTransition();

  return (
    <FormWithAction
      action={action}
      withRecaptcha={withRecaptcha}
      onSuccess={(success) => {
        if (onSuccess) {
          onSuccess(success);
        }
        // Only navigate if the success message is a path or an external URL
        if (success.message.startsWith('/')) {
          navigate(success.message);
        }
        if (isExternalUrl(success.message)) {
          window.location.href = processUrlParams(success.message);
        }
      }}
      renderContent={(state) => (
        <>
          {renderContent(state)}
          {!hideFormError ? <FormGenericError formState={state} /> : null}
          {typeof CustomFooter !== 'undefined' ? (
            CustomFooter
          ) : (
            <footer className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
              <SubmitButton fullWidth data-testid="StepForm-submit">
                {submitLabel ?? 'Continue'}
              </SubmitButton>
            </footer>
          )}
        </>
      )}
    />
  );
};
